import React, { useCallback, useEffect, useState } from 'react';
import { Check, Clock } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getProfessorToken } from '../authCookie';
import { apiErrorText } from '@/lib/apiError';
import { confusionColor } from '@/components/ConfusionMeter';

interface UnderstandingSummary {
  count: number;
  average?: number | null;
  buckets: number[];
  pct25?: number | null;
  median?: number | null;
  pct75?: number | null;
}

interface PromptInfo {
  prompt_id: string;
  prompt_text: string;
  position: number;
  is_closed: boolean;
  understanding: UnderstandingSummary;
}

interface QuestionInfo {
  question_id: string;
  question_text: string;
  votes: number;
  is_answered: boolean;
}

interface ReviewProfessorInterfaceProps {
  sessionId: string;
  expiresAt?: string | null;
  backendUrl?: string;
  /** Called after the session is ended, so the parent can leave the view. */
  onEnded?: () => void;
}

const POLL_MS = 5000;
// Bucket midpoints (0-19, 20-39, ...), used to colour the histogram segments.
const BUCKET_MIDPOINTS = [10, 30, 50, 70, 90];

function authHeaders(): Record<string, string> {
  const t = getProfessorToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtStat(v?: number | null): string {
  return v == null ? '–' : String(Math.round(v));
}

/** A compact 5-segment histogram of understanding levels for one prompt. */
function UnderstandingBar({ summary }: { summary: UnderstandingSummary }) {
  const total = summary.count;
  if (total === 0) {
    return <p className="text-xs text-muted-foreground">No responses yet.</p>;
  }
  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {summary.buckets.map((n, i) => (
          <div
            key={i}
            style={{ width: `${(n / total) * 100}%`, backgroundColor: confusionColor(BUCKET_MIDPOINTS[i]) }}
            title={`${n} at ${i * 20}-${i * 20 + 19}`}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {total} response{total === 1 ? '' : 's'} · avg {fmtStat(summary.average)} ·
        {' '}p25 {fmtStat(summary.pct25)} · median {fmtStat(summary.median)} · p75 {fmtStat(summary.pct75)}
      </p>
    </div>
  );
}

export default function ReviewProfessorInterface({
  sessionId,
  expiresAt,
  backendUrl,
  onEnded,
}: ReviewProfessorInterfaceProps) {
  const [prompts, setPrompts] = useState<PromptInfo[]>([]);
  const [questions, setQuestions] = useState<QuestionInfo[]>([]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [expiry, setExpiry] = useState<string | null>(expiresAt ?? null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [pRes, qRes] = await Promise.all([
        fetch(`${backendUrl}/api/prompts/${sessionId}`, { headers: authHeaders() }),
        fetch(`${backendUrl}/api/questions/${sessionId}`, { headers: authHeaders() }),
      ]);
      if (pRes.ok) setPrompts((await pRes.json()).prompts || []);
      if (qRes.ok) setQuestions((await qRes.json()).questions || []);
    } catch {
      // Transient; next poll retries.
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, sessionId]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const postPrompt = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setIsPosting(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId, prompt_text: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(apiErrorText(data?.detail, 'Could not post the prompt.'));
        return;
      }
      setDraft('');
      refresh();
    } catch {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  const closePrompt = async (promptId: string) => {
    try {
      await fetch(`${backendUrl}/api/prompts/${promptId}/close`, {
        method: 'POST',
        headers: authHeaders(),
      });
      refresh();
    } catch {
      setError('Could not close the prompt.');
    }
  };

  const markAnswered = async (questionId: string) => {
    try {
      await fetch(`${backendUrl}/api/questions/mark-answered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId, question_id: questionId }),
      });
      refresh();
    } catch {
      setError('Could not mark the question answered.');
    }
  };

  const extend = async () => {
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/sessions/${sessionId}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(apiErrorText(data?.detail, 'Could not extend the session.'));
        return;
      }
      setExpiry(data.expires_at ?? expiry);
    } catch {
      setError('Unable to reach the server. Please try again.');
    }
  };

  const endSession = async () => {
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/session/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(apiErrorText(data?.detail, 'Could not end the session.'));
        return;
      }
      onEnded?.();
    } catch {
      setError('Unable to reach the server. Please try again.');
    }
  };

  const closesOn = expiry
    ? new Date(expiry).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const sortedQuestions = [...questions].sort((a, b) => b.votes - a.votes);

  return (
    <main className="container max-w-2xl py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/40 px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4 shrink-0" aria-hidden />
          {closesOn ? `Closes ${closesOn}` : 'Review session'}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={extend}>Extend</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive-soft-foreground hover:bg-destructive-soft">
                End session
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>End this review session?</AlertDialogTitle>
                <AlertDialogDescription>
                  Students will no longer be able to submit questions or rate prompts. The
                  understanding report is saved, and you can still view it afterward.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it open</AlertDialogCancel>
                <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={endSession}>
                  End session
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <section className="mt-8" aria-labelledby="compose-heading">
        <h2 id="compose-heading" className="text-lg font-semibold tracking-tight">Post a prompt</h2>
        <form onSubmit={postPrompt} className="mt-3 space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="A topic for students to rate their understanding of, e.g. 'Eigenvalues and eigenvectors'"
            rows={2}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={isPosting || !draft.trim()}>
              {isPosting ? 'Posting...' : 'Post prompt'}
            </Button>
          </div>
        </form>
      </section>

      <section className="mt-8" aria-labelledby="prompts-heading">
        <h2 id="prompts-heading" className="text-lg font-semibold tracking-tight">Topics</h2>
        {isLoading ? (
          <Skeleton className="mt-4 h-32 w-full rounded-2xl" />
        ) : prompts.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No prompts yet. Post one above.</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {prompts.map((p) => (
              <li key={p.prompt_id} className="rounded-2xl border bg-card p-5 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium">{p.prompt_text}</p>
                  {p.is_closed ? (
                    <Badge variant="outline">Closed</Badge>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => closePrompt(p.prompt_id)}>
                      Close
                    </Button>
                  )}
                </div>
                <div className="mt-4">
                  <UnderstandingBar summary={p.understanding} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10" aria-labelledby="questions-heading">
        <h2 id="questions-heading" className="text-lg font-semibold tracking-tight">Student questions</h2>
        {isLoading ? (
          <Skeleton className="mt-4 h-24 w-full rounded-2xl" />
        ) : sortedQuestions.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No questions submitted yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {sortedQuestions.map((q) => (
              <li key={q.question_id} className="flex items-start gap-3 rounded-xl border bg-card p-4 shadow-soft">
                <span className="flex shrink-0 flex-col items-center rounded-lg border px-2.5 py-1.5 text-sm font-semibold text-muted-foreground">
                  {q.votes}
                </span>
                <p className={`min-w-0 flex-1 text-sm ${q.is_answered ? 'text-muted-foreground line-through' : ''}`}>
                  {q.question_text}
                </p>
                {q.is_answered ? (
                  <Badge variant="success" className="shrink-0">Answered</Badge>
                ) : (
                  <Button variant="ghost" size="icon-sm" aria-label="Mark answered" onClick={() => markAnswered(q.question_id)}>
                    <Check aria-hidden />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
